import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../../../hooks/useNotification';

const editBusinessEntityWebsiteSchema = z.object({
  website: z
    .string()
    .trim()
    .min(1, { message: 'Website is required' })
    .refine((value) => {
      const urlPattern =
        /^(https?:\/\/)?([\w-]+(\.[\w-]+)+\/?)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
      return urlPattern.test(value);
    }, 'Please enter a valid website URL'),
});

type EditBusinessEntityWebsiteSchemaFields = z.infer<
  typeof editBusinessEntityWebsiteSchema
>;

export const EditBusinessEntityWebsiteModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess?: () => void;
}) => {
  const {
    watch,
    setValue,
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditBusinessEntityWebsiteSchemaFields>({
    resolver: zodResolver(editBusinessEntityWebsiteSchema),
    defaultValues: {
      website: businessEntity.generalCorporationInfo.publicFacingWebsiteUrl || '',
    },
  });

  useEffect(() => {
    setValue(
      'website',
      businessEntity.generalCorporationInfo.publicFacingWebsiteUrl || ''
    );
  }, [businessEntity.generalCorporationInfo.publicFacingWebsiteUrl, setValue]);

  const { setNotification } = useNotification();

  const close = () => {
    onClose();
    reset();
  };

  const onSave = async () => {
    if (isSubmitting) return;

    const website = watch('website');

    const res = await updateBusinessEntity(businessEntity.id, {
      publicFacingWebsiteUrl: website.trim(),
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update website',
      });
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Website updated',
    });

    if (onSuccess) await onSuccess();
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {businessEntity?.generalCorporationInfo?.publicFacingWebsiteUrl?.trim()
              ?.length
              ? 'Edit'
              : 'Add'}{' '}
            Website
          </DialogTitle>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label>
              <span>Website URL</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <Input
              id="website"
              placeholder="e.g. www.yourcompany.com"
              {...register('website')}
              error={errors.website?.message}
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit(onSave)}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
